import { Link } from "react-router-dom";
import { Truck, AlertTriangle, ClipboardX, Fuel, Gauge, Euro, UserCheck, Car, Wrench, ClipboardCheck } from "lucide-react";
import { PageHeader, Kpi, Panel, Loading, Empty, Chip, useFetch } from "@/components/common";
import { fEur, fKm, fDate, fNum } from "@/lib/api";

const sev = (s) => (s === "danger" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200");

function AlertList({ items, testid, render }) {
  if (!items.length) return <Empty text="Aucune alerte" />;
  return (
    <ul className="divide-y divide-slate-100" data-testid={testid}>
      {items.map((a) => (
        <li key={a.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">{render(a)}</li>
      ))}
    </ul>
  );
}

export default function GlobalDashboard() {
  const { data, loading } = useFetch("/dashboard/global");
  if (loading || !data) return <Loading />;
  const l = data.livraison;
  const c = data.commerciaux;
  return (
    <div data-testid="global-dashboard">
      <PageHeader overline="Direction" title="Tableau de bord général" />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-8">
        <Kpi testid="kpi-fleet-total" label="Véhicules en parc" value={data.fleet.total} sub={`${data.fleet.livraison} livraison · ${data.fleet.commercial} commerciaux`} icon={Car} tone="navy" />
        <Kpi testid="kpi-materiel-checks-due" label="Contrôles matériel entrepôt à faire" value={data.materiel_checks_due} sub="Ce mois-ci" icon={ClipboardCheck} />
        <Kpi testid="kpi-open-anomalies" label="Anomalies non résolues" value={l.open_anomalies} icon={AlertTriangle} tone="danger" delay={1} />
        <Kpi testid="kpi-spend" label="Dépenses carburant" value={fEur(c.spend, 0)} sub={`Mois en cours : ${fEur(c.spend_month, 0)}`} icon={Euro} delay={2} />
        <Kpi testid="kpi-perso" label="Km personnels" value={fKm(c.perso_km)} icon={UserCheck} delay={3} />
        <Kpi testid="kpi-maintenance-due" label="Entretiens à prévoir" value={data.maintenance_due} sub={`dont ${data.maintenance_overdue} en retard`} icon={Wrench} tone="danger" delay={4} />
      </div>
      <Panel title="Rappels d'entretien" className="mb-6" testid="global-maintenance-alerts">
        <AlertList items={data.maintenance_alerts} testid="global-maint-list" render={(a) => (
          <>
            <span className="flex items-center gap-2 min-w-0"><Chip cls={a.status === "en_retard" ? sev("danger") : sev("warning")}>{a.plate}</Chip><span className="truncate">{a.type_label} — {a.status_label} ({a.reason})</span></span>
            <Link to={a.vehicle_type === "livraison" ? "/livraison/entretien" : "/commerciaux/entretien"} className="text-xs font-semibold text-[#E0830B] shrink-0">{fDate(a.next_due_date)}</Link>
          </>
        )} />
      </Panel>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Véhicules de livraison" action={<Link to="/livraison/dashboard" className="text-xs font-semibold text-[#E0830B]" data-testid="link-liv-dashboard">Ouvrir →</Link>}>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <MiniStat icon={Truck} label="Actifs" v={l.active_vehicles} />
            <MiniStat icon={AlertTriangle} label="Avec anomalie" v={l.vehicles_with_anomaly} />
            <MiniStat icon={ClipboardX} label="Contrôles manquants" v={l.missing_checks} />
          </div>
          <AlertList items={data.livraison_alerts} testid="global-liv-alerts" render={(a) => (
            <>
              <span className="flex items-center gap-2 min-w-0"><Chip cls={sev(a.severity)}>{a.plate}</Chip><span className="truncate">{a.label}</span></span>
              <span className="text-xs text-slate-400 shrink-0">{fDate(a.date)}</span>
            </>
          )} />
        </Panel>
        <Panel title="Véhicules commerciaux" action={<Link to="/commerciaux/dashboard" className="text-xs font-semibold text-[#E0830B]" data-testid="link-com-dashboard">Ouvrir →</Link>}>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <MiniStat icon={Gauge} label="Km parcourus" v={fNum(c.km)} />
            <MiniStat icon={Fuel} label="Prix moyen / L" v={c.ppl ? `${fNum(c.ppl, 3)} €` : "—"} />
            <MiniStat icon={Euro} label="Coût / 100 km" v={fEur(c.cost100)} />
          </div>
          <AlertList items={data.commerciaux_alerts} testid="global-com-alerts" render={(a) => (
            <>
              <span className="flex items-center gap-2 min-w-0"><Chip cls={sev(a.severity)}>{a.user_name}</Chip><span className="truncate">{a.label}</span></span>
              <span className="text-xs text-slate-400 shrink-0">{fDate(a.date)}</span>
            </>
          )} />
        </Panel>
      </div>
    </div>
  );
}

const MiniStat = ({ icon: Icon, label, v }) => (
  <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
    <Icon className="w-4 h-4 text-[#F49619]" />
    <div className="font-display text-xl font-bold text-[#0E2D43] mt-1.5 tabular-nums">{v}</div>
    <div className="text-[11px] text-slate-500">{label}</div>
  </div>
);
